import { useEffect, useMemo, useRef } from "react";
import gsap from "gsap";

type Props = {
  text?: string;
  lines?: string[];
  className?: string;
  typingSpeed?: number;
  startDelay?: number;
  cursorColor?: string;
};

function Line({ content, speed, delay, cursorColor }: { content: string; speed: number; delay: number; cursorColor: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLSpanElement>(null);

  const chars = useMemo(() => content.split(""), [content]);

  useEffect(() => {
    if (!containerRef.current) return;
    const spans = Array.from(containerRef.current.querySelectorAll("span[data-char='1']"));
    const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
    tl.set(spans, { opacity: 0, y: 6 });
    tl.to(spans, { opacity: 1, y: 0, stagger: speed, duration: 0.05, delay }, 0);
    let cursorTween: gsap.core.Tween | null = null;
    if (cursorRef.current) {
      cursorTween = gsap.to(cursorRef.current, { opacity: 0, repeat: -1, yoyo: true, duration: 0.6, ease: "power1.inOut" });
    }
    tl.eventCallback("onComplete", () => {
      if (cursorRef.current) {
        cursorTween?.kill();
        gsap.set(cursorRef.current, { display: "none" });
      }
    });
    return () => {
      tl.kill();
      cursorTween?.kill();
    };
  }, [speed, delay]);

  return (
    <div ref={containerRef} className="relative inline-block md:hitespace-nowrap">
      {chars.map((c, i) => (
        <span
          key={i}
          data-char="1"
          className={c === " " ? "inline-block align-baseline mx-1" : "inline-block align-baseline"}
        >
          {c === " " ? "\u00A0" : c}
        </span>
      ))}
      <span ref={cursorRef} className="inline-block align-baseline ml-1" style={{ width: 2, height: "1em", backgroundColor: cursorColor }} />
    </div>
  );
}

export default function TypewriterText({ text, lines, className, typingSpeed = 0.03, startDelay = 0, cursorColor = "#8B5CF6" }: Props) {
  const items = lines && lines.length ? lines : text ? [text] : [];
  return (
    <div className={className}>
      {items.map((line, idx) => (
        <div key={idx} className="block">
          <Line content={line} speed={typingSpeed} delay={startDelay + idx * 0.2} cursorColor={cursorColor} />
        </div>
      ))}
    </div>
  );
}
